/**
 * Renders a form from AI response: fields, validation, submit to AI tool flow.
 * Uses Bootstrap Form controls consistent with WizardCreation.
 * For submit_tool "create_enquiry", submits directly to /enquiry/create_new_enquiry so field_errors
 * from the API are shown on the correct inputs (the chat message round-trip does not return them).
 */
import React, { useState, useEffect } from "react";
import { Card, Form, Button, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { fetchWithAuth } from "../FetchWithAuth";
import { url } from "../url";
import { getFormFieldId, mapApiFieldErrors } from "../createEnquiryFieldErrors";

const FIELD_TYPES = ["text", "email", "tel", "number", "date", "textarea", "select"];

const cleanDigits = (value) => String(value || "").replace(/\D/g, "");

function AIResponseForm({
  title = "Form",
  fields = [],
  submit_tool,
  submitLabel = "Submit",
  conversationId,
  onFormSubmit,
  onSuccess,
  onError,
}) {
  const [values, setValues] = useState(() => {
    const v = {};
    fields.forEach((f) => {
      const id = getFormFieldId(f);
      if (id) v[id] = f.default ?? "";
    });
    return v;
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setValues((prev) => {
      const next = { ...prev };
      let changed = false;
      fields.forEach((f) => {
        const id = getFormFieldId(f);
        if (id && next[id] === undefined) {
          next[id] = f.default ?? "";
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [fields]);

  const handleChange = (name, value) => {
    setValues((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (prev[name] == null) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validate = () => {
    const next = {};
    fields.forEach((f) => {
      const id = getFormFieldId(f);
      if (!id) return;
      const v = values[id];
      if (f.required && (v == null || String(v).trim() === "")) {
        next[id] = `${f.label || id} is required`;
      }
      if (f.type === "email" && v && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
        next[id] = "Enter a valid email";
      }
    });
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitCreateEnquiryRest = async () => {
    let counsellorRes;
    try {
      counsellorRes = await fetchWithAuth(`${url}/get_counsellors`, {
        method: "GET",
        credentials: "include",
      });
    } catch {
      throw new Error("Could not load counsellors.");
    }
    const counsellors = await counsellorRes.json().catch(() => []);
    const list = Array.isArray(counsellors) ? counsellors : [];
    const counsellor = list[0] || "";
    if (!counsellor) {
      throw new Error("No counsellor is available. Configure counsellors before creating an enquiry.");
    }

    const payload = {
      first_name: (values.first_name || "").trim(),
      last_name: (values.last_name || "").trim(),
      email: (values.email || "").trim().toLowerCase(),
      mobile: cleanDigits(values.phone || values.mobile),
      gender: (values.gender || "Other").trim() || "Other",
      telephone: cleanDigits(values.telephone || ""),
      source: (values.source || "Walk In").trim() || "Walk In",
      address: (values.address || "").trim(),
      date_of_birth: (values.date_of_birth || "").trim(),
      counsellor,
    };

    const res = await fetchWithAuth(`${url}/enquiry/create_new_enquiry`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (data?.id) return data;
    throw new Error("Enquiry created but no id was returned.");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setErrors({});
    try {
      if (submit_tool === "create_enquiry") {
        const data = await submitCreateEnquiryRest();
        toast.success(`Enquiry created (ID ${data.id})`);
        if (typeof onSuccess === "function") onSuccess(values);
      } else if (typeof onFormSubmit === "function") {
        await onFormSubmit(conversationId, submit_tool, values);
        if (typeof onSuccess === "function") onSuccess(values);
      } else if (typeof onError === "function") {
        onError("Form submission not configured (missing onFormSubmit).");
      }
    } catch (err) {
      const data = err.data || {};
      const mapped = mapApiFieldErrors(data.field_errors || {}, fields);
      const summary =
        data.error ||
        data.message ||
        (Object.keys(mapped).length ? null : err.message) ||
        "Submission failed";
      const next = { ...mapped };
      if (Object.keys(mapped).length === 0 && summary) next._form = summary;
      setErrors(next);
      if (typeof onError === "function") onError(summary);
    } finally {
      setLoading(false);
    }
  };

  if (!Array.isArray(fields) || fields.length === 0) {
    return null;
  }

  return (
    <Card className="mb-2 border shadow-sm" style={{ borderRadius: "16px" }}>
      {title && (
        <Card.Header className="py-2" style={{ backgroundColor: "#f9fafc", borderBottom: "1px solid #e9ecef" }}>
          <strong>{title}</strong>
        </Card.Header>
      )}
      <Card.Body>
        <Form onSubmit={handleSubmit} noValidate>
          {fields.map((f) => {
            const fid = getFormFieldId(f);
            if (!fid) return null;
            return (
              <Form.Group key={fid} className="mb-2">
                <Form.Label className="small">
                  {f.label || fid}
                  {f.required && " *"}
                </Form.Label>
                {f.type === "textarea" ? (
                  <Form.Control
                    as="textarea"
                    rows={2}
                    size="sm"
                    value={values[fid] ?? ""}
                    onChange={(e) => handleChange(fid, e.target.value)}
                    isInvalid={!!errors[fid]}
                    placeholder={f.placeholder}
                  />
                ) : f.type === "select" ? (
                  <Form.Select
                    size="sm"
                    value={values[fid] ?? ""}
                    onChange={(e) => handleChange(fid, e.target.value)}
                    isInvalid={!!errors[fid]}
                  >
                    <option value="">Select...</option>
                    {(f.options || []).map((opt) => (
                      <option key={opt.value ?? opt} value={opt.value ?? opt}>
                        {opt.label ?? opt}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <Form.Control
                    size="sm"
                    type={FIELD_TYPES.includes(f.type) ? f.type : "text"}
                    value={values[fid] ?? ""}
                    onChange={(e) => handleChange(fid, e.target.value)}
                    isInvalid={!!errors[fid]}
                    placeholder={f.placeholder}
                    required={!!f.required}
                  />
                )}
                <Form.Control.Feedback type="invalid">{errors[fid]}</Form.Control.Feedback>
              </Form.Group>
            );
          })}
          {errors._form && (
            <div className="small text-danger mb-2" role="alert">
              {errors._form}
            </div>
          )}
          <Button type="submit" size="sm" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-1" />
                Submitting…
              </>
            ) : (
              submitLabel
            )}
          </Button>
        </Form>
      </Card.Body>
    </Card>
  );
}

export default AIResponseForm;
